import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { MainTableFilterModel } from 'src/app/shared/models/main-table-filter.model';
import { ActivityStatus } from 'src/app/shared/enums/activity-status.enum';
import { MainTableFilteringHelperService } from 'src/app/shared/main-table-module/services/main-table-filtering-helper.service';
import { createFilterParams } from 'src/app/shared/functions/create-filter-params';
import { environment } from '../../../environments/environment';
import {
  IApiResponseGroupDrills,
  IDrill,
  IDrillCreate,
  IDrillListItem,
  IDrillPerformRequest,
  IDrillPostpone,
  IDrillPerformResponse,
} from '../models/drills.model';
import { getDepartmentId, getTenantId } from '../../services/store-snapshot.service';
import { ApiResponse } from '../../core/models/api-response';
import { IdName } from '../../shared/models/id-name';
import { DrillsOverview } from '../models/drills-overview.model';
import { DrillLog } from '../models/drills-log.model';
import { FormDataService } from '../../shared/attachments/service/form-data.service';

const COLUMN_TO_KEY_MAP: Record<string, string> = {
  expiry: 'ExpiryDate',
  accessPositions: 'PositionsWithAccess.Name',
  accessPositionGroups: 'PositionGroupsWithAccess.Name',
  accessRoles: 'RolesWithAccess.Name',
  lastPerformed: 'LastDrillDate',
  pausable: 'Pausable',
  responsiblePositions: 'ResponsibleForPositions.Name',
  responsiblePositionGroups: 'ResponsibleForPositionGroups.Name',
  responsibleRoles: 'ResponsibleForRoles.Name',
};
const OR_GROUP_MAP: Record<string, string> = {
  accessPositions: 'access',
  accessPositionGroups: 'access',
  accessRoles: 'access',
  responsiblePositions: 'responsible',
  responsiblePositionGroups: 'responsible',
  responsibleRoles: 'responsible',
};
@Injectable({ providedIn: 'root' })
export class DrillsApiService {
  private get url(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${getDepartmentId()}/drills`;
  }

  private get drillsForIOUrl(): string {
    return `${this.url}/Interconnected`;
  }

  constructor(private http: HttpClient) {}

  loadDrills(): Observable<ApiResponse<IDrillListItem[]>> {
    return this.http.get<ApiResponse<IDrillListItem[]>>(this.url);
  }

  loadDrillsForGrouping(currentFilter?: MainTableFilterModel): Observable<IApiResponseGroupDrills> {
    const url = `${this.url}/menu/paged`;
    let params = createFilterParams(currentFilter);

    if (currentFilter?.additional?.menuId) {
      params = params.set('menuId', currentFilter.additional?.menuId);
    }
    if (currentFilter?.additional?.activityStatus === ActivityStatus.Deactivated) {
      params = params.set('IsDisabled', 'true');
    }

    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      currentFilter,
      COLUMN_TO_KEY_MAP,
      OR_GROUP_MAP
    );

    return this.http.get<IApiResponseGroupDrills>(url, { params });
  }

  loadDrillById(drillId: string): Observable<IDrill> {
    return this.http.get<IDrill>(`${this.url}/${drillId}`);
  }

  loadDrillVersionById(drillId: string, drillVersionId: string): Observable<IDrillPerformResponse> {
    return this.http.get<IDrillPerformResponse>(`${this.url}/${drillId}/log/${drillVersionId}`);
  }

  loadDrillLog(drillId: string): Observable<DrillLog> {
    return this.http.get<DrillLog>(`${this.url}/${drillId}/log`);
  }

  loadDrillsByMenuId(menuId: string): Observable<DrillsOverview[]> {
    let url;
    if (menuId) {
      url = `${this.url}/menu?menuId=${menuId}`;
    } else {
      url = `${this.url}/menu`;
    }
    return this.http.get<DrillsOverview[]>(url);
  }

  createDrill(drill: IDrillCreate): Observable<IdName> {
    const formData = FormDataService.mapToFormData(drill);
    return this.http.post<IdName>(this.url, formData);
  }

  updateDrill(drill: IDrillCreate): Observable<void> {
    const formData = FormDataService.mapToFormData(drill);
    return this.http.put<void>(`${this.url}/${drill.id}`, formData);
  }

  deleteDrill(drillId: string): Observable<void> {
    return this.http.delete<void>(`${this.url}/${drillId}`);
  }

  performDrill(drill: IDrillPerformRequest): Observable<void> {
    const formData = FormDataService.mapToFormData(drill);
    const url = `${this.url}/${drill.id}/perform/v2`;
    return this.http.put<void>(url, formData);
  }

  postponeDrill({ id, newExpiryDate, comment }: IDrillPostpone): Observable<void> {
    return this.http.put<void>(`${this.url}/${id}/postpone`, { newExpiryDate, comment });
  }

  loadDrillDraftPerform(drillId: string): Observable<IDrillPerformResponse> {
    return this.http.get<IDrillPerformResponse>(`${this.url}/${drillId}/draftPerform`);
  }

  saveDrillDraftPerform(drill: IDrillPerformRequest): Observable<void> {
    const formData = FormDataService.mapToFormData(drill);
    return this.http.put<void>(`${this.url}/${drill.id}/draftPerform`, formData);
  }

  getDrillsForIO(): Observable<IdName[]> {
    return this.http.get<IdName[]>(this.drillsForIOUrl);
  }

  deactivateDrill(id: string): Observable<void> {
    const url = `${this.url}/${id}/disable`;
    return this.http.put<void>(url, { id });
  }

  reactivateDrill(id: string): Observable<void> {
    const url = `${this.url}/${id}/enable`;
    return this.http.put<void>(url, { id });
  }
}
